import { useEffect } from 'react';
import { getSeoMeta, SITE_URL } from '../data/seoMeta';

/**
 * Lightweight SEO component - manages document.title, meta description,
 * meta keywords, Open Graph tags and an optional JSON-LD schema block.
 *
 * Usage:
 *   <SEO title="..." description="..." keywords="..." canonical="/programs/mbbs"
 *        image="https://..." jsonLd={{...}} />
 */

const upsertMeta = (attr, key, value) => {
  if (!value) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
};

const upsertLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const upsertJsonLd = (id, schema) => {
  let el = document.getElementById(id);
  if (!schema) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(schema);
};

const SEO = ({ title: titleProp, description: descProp, keywords, canonical, image, jsonLd }) => {
  const meta = getSeoMeta(canonical);
  const title = meta?.title || titleProp;
  const description = meta?.description || descProp;
  useEffect(() => {
    if (title) document.title = title;
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', keywords);
    const url = canonical ? `${SITE_URL}${canonical}` : null;
    upsertLink('canonical', url);

    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', image);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image);

    upsertJsonLd('page-jsonld', jsonLd);
  }, [title, description, keywords, canonical, image, jsonLd]);

  return null;
};

export default SEO;
